import React, { useMemo, useState, useEffect } from 'react';
import * as THREE from 'three';
import { createHexagonShape, createHexOutlineGeometry } from '../geometries/hexagonGeometry';
import audioManager from '../../../../../utils/filemanager/AudioManager';
import accountStore from '../../../../../components/account/AccountStore';

function RealityPlanes({
  numPlanes = 100,
  spacing = 1,
  radius = 4,
  basePosition = [0, 0, 0],
  onPlaneClick
}) {
  const [hoveredPlane, setHoveredPlane] = useState(null);
  const hexOutlineGeometry = useMemo(() => createHexOutlineGeometry(), []);

  // Only play sound when hoveredPlane actually changes
  useEffect(() => {
    if (hoveredPlane !== null && audioManager.isInitialized()) {
      audioManager.playHover();
    }
  }, [hoveredPlane]);

  useEffect(() => {
    // Initialize audio system
    if (!audioManager.isInitialized()) {
      audioManager.init().catch(console.error);
    }
  }, []);

  // Subscribe to account store changes to update when rank changes
  useEffect(() => {
    const unsubscribe = accountStore.subscribe(() => {
      // Force re-render when account store changes
      setHoveredPlane(null); // Reset hover state
    });
    return () => unsubscribe();
  }, []);

  const realityPlanes = useMemo(() => {
    const planes = [];
    const allowedHexCount = accountStore.getAllowedHexCount();

    for (let i = 0; i < numPlanes; i++) {
      const y = 1 + i * spacing;
      const opacityFactor = 1 - (i / numPlanes * 0.3);
      const isHovered = hoveredPlane === i;
      const scale = isHovered ? 1.15 : 1;
      const isAccessible = i < allowedHexCount;
      const hexColor = isHovered ? '#00ff44' : (isAccessible ? '#00ff00' : '#ff0000');
      const baseOpacity = isAccessible ? 0.02 : 0.01;
      const outlineOpacity = isAccessible ? 0.15 : 0.05;
      
      // Create materials with base opacity values
      const fillMaterial = new THREE.MeshBasicMaterial({
        color: hexColor,
        transparent: true,
        opacity: isHovered ? 0.15 : (baseOpacity * opacityFactor),
        side: THREE.DoubleSide,
        depthWrite: false // Allow rendering behind
      });

      const outlineMaterial = new THREE.LineBasicMaterial({
        color: hexColor,
        transparent: true,
        opacity: isHovered ? 0.8 : (outlineOpacity * opacityFactor),
        depthWrite: false
      });
      
      planes.push(
        <group
          key={`plane-${i}`}
          position={[0, y, 0]}
          scale={scale}
          name={`reality-plane-${i}`}
          renderOrder={0}
          onPointerEnter={(e) => {
            if (!isAccessible) return;
            e.stopPropagation();
            setHoveredPlane(i);
            document.body.style.cursor = 'pointer';
            
            // Hover effect
            fillMaterial.opacity = 0.15;
            outlineMaterial.opacity = 0.8;
          }}
          onPointerLeave={(e) => {
            if (!isAccessible) return;
            e.stopPropagation();
            setHoveredPlane(null);
            document.body.style.cursor = 'auto';
            
            // Reset to base opacity
            fillMaterial.opacity = baseOpacity * opacityFactor;
            outlineMaterial.opacity = outlineOpacity * opacityFactor;
          }}
          onClick={(e) => {
            if (!isAccessible) return;
            e.stopPropagation();
            
            // Play select sound
            if (audioManager.isInitialized()) {
              audioManager.playSelect();
            }
            
            onPlaneClick(i);
          }}
        >
          {/* Filled hex */}
          <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            name={`hex-fill-${i}`}
            renderOrder={0}
          >
            <shapeGeometry args={[createHexagonShape(radius)]} />
            <primitive object={fillMaterial} />
          </mesh>

          {/* Outline */}
          <lineSegments name={`hex-outline-${i}`} renderOrder={0}>
            <bufferGeometry {...hexOutlineGeometry} />
            <primitive object={outlineMaterial} />
          </lineSegments>

          {/* Additional glow plane on hover */}
          {isHovered && isAccessible && (
            <mesh 
              rotation={[-Math.PI / 2, 0, 0]} 
              name={`hex-glow-${i}`}
              renderOrder={0}
            >
              <shapeGeometry args={[createHexagonShape(radius * 1.05)]} />
              <meshBasicMaterial
                color={hexColor}
                transparent={true}
                opacity={0.1}
                side={THREE.DoubleSide}
                depthWrite={false}
              />
            </mesh>
          )}
        </group>
      );
    }
    return planes;
  }, [hexOutlineGeometry, hoveredPlane, numPlanes, spacing, radius]);

  return (
    <group position={basePosition} name="reality-planes-container" renderOrder={0}>
      {realityPlanes}
    </group>
  );
}

export default RealityPlanes;